import { Form } from './form';
import { Config } from '../config';
import { FormTypes } from './form-types';
import { StripeAchView } from '../view/stripe-ach-view';
import { DepositfixProvider } from '../providers/depositfix-provider';
import { Util } from '../config/utils';
import { Order } from '../config/order';
import { PaymentType } from '../providers/payment-type';

declare const Plaid: any;

export class StripeAchForm extends Form {

    provider: DepositfixProvider;
    view: StripeAchView;
    type = FormTypes.Stripe_Ach;
    paymentType = PaymentType.STRIPE_ACH;
    isNoTotal = false;
    token: any;
    publicToken: any;
    accountId: any;
    errorMessage: any;
    achConfig: any;
    achHandler: any;

    constructor(step: number | null = null) {
        super();
        this.step = step;
        this.provider = new DepositfixProvider();
        this.view = new StripeAchView();
    }

    async init(config: Config, callback: any) {
        await super.init(config, callback);
        await this.loadScript('https://cdn.plaid.com/link/v2/stable/link-initialize.js');
        this.fetchToken()
            .then(async (response: any) => {
                this.token = response.token;
                await this.addAchHandler();
                this.addPaymentButtonSubmitEvent();
            })
            .catch(async (error: any) => {
                const response = await error.json();
                this.logger.log(response);
                this.errorMessage = response.errorMessage;
                this.setErrorMessageText(this.errorMessage);
            });
        return Promise.resolve();
    }

    fetchToken = async () => {
        let url = `${process.env.API_URL}pub/plaid/${this.config.user.portalId}/token`;
        if (Util.isForcedTestMode()) {
            url += `?mode=test`;
        }
        return this.provider.fetch(url);
    }

    addAchHandler() {
        return new Promise<void>(async resolve => {
            this.achConfig = this.getConfig();
            this.achHandler = await Plaid.create(this.achConfig);
            resolve();
        });
    }

    getConfig() {
        return {
            clientName: this.config.forms.stripe.ach.clientName,
            env: this.config.forms.stripe.ach.environment,
            product: ['auth'],
            token: this.token,

            onSuccess: async (publicToken: string, metadata: any) => {
                this.publicToken = publicToken;
                this.accountId = metadata.accounts[0].id;
                await this.config.widget.submit();
            },

            onExit: async (error: any, metadata: any) => {
                if (error !== null) {
                    this.logger.log(error);
                    if (error.error_code === 'INVALID_LINK_TOKEN') {
                        this.refreshToken();
                    } else {
                        this.view.showError(error.error_message);
                    }
                }
            }
        };
    }

    async refreshToken() {
        const response = await this.fetchToken();
        this.token = response.token;
        this.achConfig.token = this.token;
        this.achHandler.destroy();
        this.achHandler = await Plaid.create(this.achConfig);
    }

    addPaymentButtonSubmitEvent() {
        const stripeAchPaymentButton = this.view.getHtmlElement('#stripe_ach_button');
        stripeAchPaymentButton.addEventListener('click', async (event: any) => {
            event.preventDefault();
            if (await this.config.widget.validate()) {
                this.achHandler.open();
            }
        });
    }

    render() {
        if (this.step) {
            this.hideForm();
        }
        return Promise.resolve();
    }

    renderForm() {
        this.view.renderForm();
        if (this.errorMessage) {
            this.view.showError(this.errorMessage);
        }
    }

    setErrorMessageText(errorMessage: string) {
        this.view.setErrorMessageText(errorMessage);
    }

    submit(order: Order) {
        const contact = order.params[0];
        order.paymentMethod = {
            type: this.paymentType,
            stripeEmail: contact.email,
            accountId: this.config.forms.stripe.accountId ? this.config.forms.stripe.accountId : null,
            achPublicToken: this.publicToken,
            achAccountId: this.accountId
        };
        return Promise.resolve(order);
    }

    postSubmit(submissionContext: any): Promise<any> {
        this.config.widget.triggerAppEvent('onFormSubmitted', submissionContext);
        this.config.widget.redirectOrMessage();
        return Promise.resolve();
    }

    showForm() {
        this.view.showForm();
    }

    hideForm() {
        this.view.hideForm();
    }

}
